// ====================================
// ManageClientModal.tsx
// Clean Formik + Yup inside modal
// ====================================

import React, { useCallback, useEffect, useState } from "react";
import {
  Col,
  // FormFeedback,
  // Input,
  // Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { useFormik } from "formik";
import * as Yup from "yup";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  VALIDATION_PATTERNS,
  createNameValidation,
  createAddressValidation,
  createLocationValidation,
} from "Components/constants/validation";

import { AddClientFormParams, EditClientFormParams } from "types/client.api";
import CustomInput from "Components/Custom/CustomInput";
import { useDebounce } from "Components/Hooks/useDebounce";
import Select from "react-select";
import {
  AddressOption,
  AddressType,
} from "pages/Authentication/BusinessAddress";
import { getAddressApi } from "helpers/ApiManager";
import CustomButton from "Components/Custom/CustomButton";

interface ManageModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  isEdit: boolean;
  initialData: EditClientFormParams | null;
  onSubmit: (payload: AddClientFormParams | EditClientFormParams) => void;
}

type ClientFormValues = {
  id?: number;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  streetAddress: string;
  city: string;
  zipCode: string;
  country: string;
};

const ManageClientModal: React.FC<ManageModalProps> = ({
  isOpen,
  toggleModal,
  isEdit,
  initialData,
  onSubmit,
}) => {
  const [searchPostcode, setSearchPostcode] = useState<string>("");
  const debounceSearchPostcode = useDebounce(searchPostcode, 500);
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchPostcodeData = async () => {
      try {
        setLoading(true);
        const res = await getAddressApi(debounceSearchPostcode);
        if (res) {
          console.log("Address Data:", JSON.stringify(res?.payload));
          const options: AddressOption[] = res?.payload?.map(
            (ads: AddressType) => ({
              label: ads?.formatted_address,
              value: `${ads?.postcode}-${ads?.formatted_address}`,
              raw: ads,
            }),
          );
          console.log("Address Options:", options);

          setAddressOptions(() => options);
        }
      } catch (error) {
        console.error("Error fetching address data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (debounceSearchPostcode && debounceSearchPostcode.length > 3) {
      fetchPostcodeData();
    }
  }, [debounceSearchPostcode]);

  const handleSearch = useCallback((value: string) => {
    setSearchPostcode(value);
  }, []);

  const handleClear = useCallback(() => {
    setAddressOptions([]);
  }, []);

  // Helper function to format phone number from backend
  const formatPhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) return "";

    // Remove any existing formatting
    const cleanValue = phone.replace(/[^\d]/g, "");

    if (cleanValue.length === 0) return "";

    // Check if it already has country code
    const countryCode = cleanValue.startsWith("44")
      ? cleanValue.substring(0, 2)
      : "44";
    const phoneNumber = cleanValue.startsWith("44")
      ? cleanValue.substring(2)
      : cleanValue;

    // Return formatted as (+44) XXXXXXXXXX (with space)
    return `(+${countryCode}) ${phoneNumber}`;
  };

  // Handle phone change with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-digit characters
    const cleanValue = value.replace(/[^\d]/g, "");

    // Build the formatted value
    let formattedValue = "";
    if (cleanValue.length > 0) {
      // Add country code
      const countryCode = cleanValue.startsWith("44")
        ? cleanValue.substring(0, 2)
        : "44";
      const phoneNumber = cleanValue.startsWith("44")
        ? cleanValue.substring(2)
        : cleanValue;

      // Format as (+44) XXXXXXXXXX for display (with space)
      formattedValue = `(+${countryCode}) ${phoneNumber}`;
    }

    // Update formik with formatted value for display
    formik.setFieldValue("phone", formattedValue);
  };

  // =========================
  // Formik Validation Schema
  // =========================
  const nameValidation = createNameValidation("Name");
  // const companyNameValidation = createNameValidation("Company Name");

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(VALIDATION_MESSAGES.REQUIRED("name"))
      .trim()
      .min(
        VALIDATION_LIMITS.NAME.MIN,
        VALIDATION_MESSAGES.MIN_LENGTH("Name", VALIDATION_LIMITS.NAME.MIN),
      )
      .max(
        VALIDATION_LIMITS.NAME.MAX,
        VALIDATION_MESSAGES.MAX_LENGTH("Name", VALIDATION_LIMITS.NAME.MAX),
      )
      .test("valid-name", nameValidation.message, nameValidation.test),
    companyName: Yup.string()
      // .required(VALIDATION_MESSAGES.REQUIRED("company name"))
      .trim()
      // .min(
      //   VALIDATION_LIMITS.NAME.MIN,
      //   VALIDATION_MESSAGES.MIN_LENGTH(
      //     "Company Name",
      //     VALIDATION_LIMITS.NAME.MIN,
      //   ),
      // )
      .max(
        VALIDATION_LIMITS.NAME.MAX,
        VALIDATION_MESSAGES.MAX_LENGTH(
          "Company Name",
          VALIDATION_LIMITS.NAME.MAX,
        ),
      ),
    // .test(
    //   "valid-company-name",
    //   companyNameValidation.message,
    //   companyNameValidation.test,
    // )
    email: Yup.string()
      .required(VALIDATION_MESSAGES.REQUIRED("email address"))
      .trim()
      .email(VALIDATION_MESSAGES.INVALID_EMAIL),
    phone: Yup.string()
      .required(VALIDATION_MESSAGES.REQUIRED("phone number"))
      .matches(VALIDATION_PATTERNS.PHONE, VALIDATION_MESSAGES.INVALID_PHONE),
    // address: Yup.string()
    //   .required(VALIDATION_MESSAGES.REQUIRED("address"))
    //   .trim()
    //   .min(
    //     VALIDATION_LIMITS.ADDRESS.MIN,
    //     VALIDATION_MESSAGES.MIN_LENGTH("Address", VALIDATION_LIMITS.ADDRESS.MIN)
    //   )
    //   .max(
    //     VALIDATION_LIMITS.ADDRESS.MAX,
    //     VALIDATION_MESSAGES.MAX_LENGTH("Address", VALIDATION_LIMITS.ADDRESS.MAX)
    //   )
    //   .test("valid-address", addressValidation.message, addressValidation.test),
    streetAddress: Yup.string()
      .trim()
      .required(VALIDATION_MESSAGES.REQUIRED("your street address"))
      .min(
        VALIDATION_LIMITS.ADDRESS.MIN,
        VALIDATION_MESSAGES.MIN_LENGTH(
          "Street Address",
          VALIDATION_LIMITS.ADDRESS.MIN,
        ),
      )
      .max(
        VALIDATION_LIMITS.ADDRESS.MAX,
        VALIDATION_MESSAGES.MAX_LENGTH(
          "Street Address",
          VALIDATION_LIMITS.ADDRESS.MAX,
        ),
      )
      .test(
        "valid-address",
        createAddressValidation("Street Address").message,
        createAddressValidation("Street Address").test,
      ),
    city: Yup.string()
      .trim()
      .required(VALIDATION_MESSAGES.REQUIRED("your city"))
      .min(
        VALIDATION_LIMITS.LOCATION.MIN,
        VALIDATION_MESSAGES.MIN_LENGTH("City", VALIDATION_LIMITS.LOCATION.MIN),
      )
      .max(
        VALIDATION_LIMITS.LOCATION.MAX,
        VALIDATION_MESSAGES.MAX_LENGTH("City", VALIDATION_LIMITS.LOCATION.MAX),
      )
      .test(
        "valid-city",
        createLocationValidation("City").message,
        createLocationValidation("City").test,
      ),
    zipCode: Yup.string()
      .required(VALIDATION_MESSAGES.REQUIRED("your postcode"))
      .matches(
        VALIDATION_PATTERNS.ZIP_CODE,
        "Postcode must contain only letters, numbers, and spaces",
      )
      .max(
        VALIDATION_LIMITS.ZIP_CODE.MAX,
        VALIDATION_MESSAGES.MAX_LENGTH(
          "Postcode",
          VALIDATION_LIMITS.ZIP_CODE.MAX,
        ),
      ),
    country: Yup.string()
      .trim()
      .required(VALIDATION_MESSAGES.REQUIRED("your country"))
      .min(
        VALIDATION_LIMITS.LOCATION.MIN,
        VALIDATION_MESSAGES.MIN_LENGTH(
          "Country",
          VALIDATION_LIMITS.LOCATION.MIN,
        ),
      )
      .max(
        VALIDATION_LIMITS.LOCATION.MAX,
        VALIDATION_MESSAGES.MAX_LENGTH(
          "Country",
          VALIDATION_LIMITS.LOCATION.MAX,
        ),
      )
      .test(
        "valid-country",
        createLocationValidation("Country").message,
        createLocationValidation("Country").test,
      ),
  });

  // =========================
  // Formik Setup
  // =========================
  const formik = useFormik<ClientFormValues>({
    enableReinitialize: true,
    initialValues: {
      id: initialData?.id,
      name: initialData?.name ?? "",
      companyName: initialData?.company_name ?? "",
      email: initialData?.email ?? "",
      phone: formatPhoneNumber(initialData?.phone),
      // address: initialData?.address ?? "",
      streetAddress: initialData?.address ?? "",
      city: initialData?.city ?? "",
      zipCode: initialData?.postcode ?? "",
      country: initialData?.country ?? "",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      // Clean phone number for backend (remove parentheses and spaces, keep +44)
      const phoneNumber = values.phone.replace(/[()]/g, "").replace(/\s+/g, "");

      // create final payload based on mode
      const payload: AddClientFormParams | EditClientFormParams = isEdit
        ? {
            ...values,
            name: values.name?.trim(),
            company_name: values.companyName?.trim(),
            email: values.email?.trim(),
            address: values.streetAddress?.trim(),
            city: values.city?.trim(),
            postcode: values.zipCode?.trim(),
            country: values.country?.trim(),
            phone: phoneNumber,
            id: values.id!,
          } // must include id
        : {
            ...values,
            name: values.name?.trim(),
            company_name: values.companyName?.trim(),
            email: values.email?.trim(),
            address: values.streetAddress?.trim(),
            city: values.city?.trim(),
            postcode: values.zipCode?.trim(),
            country: values.country?.trim(),
            phone: phoneNumber,
          }; // no id for add

      onSubmit(payload);
      formik.resetForm();
      toggleModal();
      resetForm();
    },
  });

  return (
    <Modal
      centered
      isOpen={isOpen}
      // toggle={toggleModal}
      contentClassName="br-8"
    >
      <ModalHeader
        className="bg-greyF5 p-3 bg-light  br-top-8"
        toggle={() => {
          formik.resetForm();
          toggleModal?.();
        }}
      >
        {isEdit ? "Edit Client" : "Add Client"}
      </ModalHeader>

      <form
        className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <ModalBody>
          <Row>
            <Col lg={12}>
              <CustomInput
                label="Client Name"
                placeholder="Full name"
                name="name"
                type="text"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.name}
                error={formik.errors.name}
              />
              <CustomInput
                label="Company Name"
                placeholder="Company name"
                name="companyName"
                type="text"
                value={formik.values.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.companyName}
                error={formik.errors.companyName}
              />
              <CustomInput
                label="Phone"
                placeholder="Enter phone number"
                name="phone"
                type="text"
                value={formik.values.phone}
                onChange={handlePhoneChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.phone}
                error={formik.errors.phone}
              />
              <CustomInput
                label="Email"
                placeholder="Email address"
                name="email"
                type="text"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.email}
                error={formik.errors.email}
              />
              <div className="divider text-black2D" />
              <div className="mb-3 mt-0">
                <Select
                  value={searchPostcode}
                  inputValue={searchPostcode}
                  filterOption={() => true}
                  onChange={(
                    selectedOption: AddressOption,
                    actionMeta: any,
                  ) => {
                    if (actionMeta.action === "clear") {
                      handleClear();
                      return;
                    }

                    if (!selectedOption) {
                      return;
                    }

                    const rawData = selectedOption?.raw;

                    const fullStreetAddress = [
                      rawData?.address_line_1,
                      rawData?.address_line_2,
                      rawData?.address_line_3,
                      rawData?.address_line_4,
                    ]
                      .map((line) => line?.trim())
                      .filter(Boolean)
                      .join(", ");

                    formik.setFieldValue("streetAddress", fullStreetAddress);
                    formik.setFieldValue("city", rawData?.city);
                    formik.setFieldValue("zipCode", rawData?.postcode);
                    formik.setFieldValue("country", rawData?.country);

                    setSearchPostcode("");
                    setAddressOptions([]);
                  }}
                  onInputChange={(inputValue: any) => {
                    handleSearch(inputValue);
                  }}
                  options={addressOptions}
                  placeholder="Search postcode"
                  classNamePrefix="react-select"
                  className={`auth-form-dropdown-control`}
                  isLoading={loading}
                  loadingMessage={() => <Spinner size="sm" color="primary" />}
                  noOptionsMessage={({ inputValue }: { inputValue: any }) =>
                    inputValue
                      ? `No postcode found for "${inputValue}"`
                      : "No postcode available"
                  }
                  isClearable
                  styles={{
                    menuPortal: (base: any) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    control: (base: any) => ({
                      ...base,
                      borderColor: base.borderColor,
                      backgroundColor: "#fff",
                      "&:hover": {
                        borderColor: base.borderColor,
                      },
                    }),
                  }}
                />
              </div>

              <CustomInput
                label="Street Address"
                placeholder="Street address"
                name="streetAddress"
                type="text"
                value={formik.values.streetAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.streetAddress}
                error={formik.errors.streetAddress}
              />

              <CustomInput
                label="City"
                placeholder="City"
                name="city"
                type="text"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.city}
                error={formik.errors.city}
              />

              <CustomInput
                label="Postcode"
                placeholder="Postcode"
                name="zipCode"
                type="text"
                value={formik.values.zipCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.zipCode}
                error={formik.errors.zipCode}
              />

              <CustomInput
                label="Country"
                placeholder="Country"
                name="country"
                type="text"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched.country}
                error={formik.errors.country}
              />
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter className="justify-content-between mt-0 br-bottom-8">
          <CustomButton
            text={isEdit ? "Update Client" : "Save Client"}
            type="submit"
            className="bg-primary br-7"
            loading={formik.isSubmitting}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default React.memo(ManageClientModal);
